// ========================================
// PWA Service Worker 注册
// ========================================

import { logger } from './logger';

export function registerSW() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        logger.debug('[PWA] Service Worker 注册成功:', registration.scope);
        
        // 检查更新
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          logger.debug('[PWA] 发现新版本');
          
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateNotification();
            }
          });
        });
        
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          logger.debug('[PWA] 控制器已更新');
          window.location.reload();
        });
        
      } catch (error) {
        logger.error('[PWA] Service Worker 注册失败:', error);
      }
    });
  }
}

function showUpdateNotification() {
  const notification = document.createElement('div');
  notification.id = 'pwa-update-notification';
  notification.className = 'pwa-update-notification';

  const inner = document.createElement('div');
  inner.className = 'pwa-update-inner';

  const text = document.createElement('span');
  text.textContent = '发现新版本';

  const updateBtn = document.createElement('button');
  updateBtn.className = 'pwa-update-btn';
  updateBtn.textContent = '立即更新';
  updateBtn.addEventListener('click', () => window.location.reload());

  const closeBtn = document.createElement('button');
  closeBtn.className = 'pwa-update-close';
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', () => notification.remove());

  inner.append(text, updateBtn, closeBtn);
  notification.appendChild(inner);
  document.body.appendChild(notification);
}

export async function unregisterSW() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      logger.debug('[PWA] Service Worker 已注销');
    } catch (error) {
      logger.error('[PWA] Service Worker 注销失败:', error);
    }
  }
}

export function isPWAInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function initInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    logger.debug('[PWA] 安装提示已禁用');
  });

  window.addEventListener('appinstalled', () => {
    logger.debug('[PWA] 应用已安装');
  });
}

export async function promptInstall() {
  logger.debug('[PWA] 安装提示已禁用');
}

export function initPWA() {
  // 开发环境、HTTP 部署（华为云现网）、或显式关闭时禁用 SW，避免缓存旧版 JS 导致 satoken/Bearer 混用 401
  const disableSw =
    import.meta.env.DEV ||
    import.meta.env.VITE_DISABLE_SW === 'true' ||
    (typeof window !== 'undefined' && window.location.protocol === 'http:');

  if (disableSw) {
    void (async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {
        /* ignore */
      }
    })();
    logger.debug('[PWA] Service Worker disabled (dev/http/explicit); cleared registrations and caches');
    return;
  }

  registerSW();
  initInstallPrompt();

  if (isPWAInstalled()) {
    logger.debug('[PWA] 应用已安装为PWA');
  }
}

export default {
  init: initPWA,
  register: registerSW,
  unregister: unregisterSW,
  isInstalled: isPWAInstalled,
  promptInstall
};
